import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import NumberFormat from "react-number-format";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook, useModals } from "hooks";
import { useMeasureDrawer } from "hooks/debt-hooks/use-measure-drawer";
import { sum } from "lodash";
import { IApiServiceError, useDateFormatSelect, useDealActions, useDebtActions, useTaskManageActions } from "store";
import {
  useDealAssetClassSelect,
  useDealDefaultCurrencySelect,
  useDealTitleSelector
} from "store/selectors/deal-selectors/deal-selectors";
import { useUserBlankTemplateIdSelector } from "store/selectors/user-selectors";
import { AmortizationRow, CurrencyType, CustomPayment, FeeType, PrepaymentPayload } from "store/types";
import { v4 as uuidv4 } from "uuid";

import { currenciesArray, currencySign } from "../../constants";
import { PrepaymentDrawerValues } from "./PrepaymentDrawerValues";
import { prepaymentValidationSchema } from "./schemas";

const textFieldStyles = {
  "& .MuiOutlinedInput-input.MuiSelect-select": {
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0
  }
};

const inputStyles = {
  "& .MuiOutlinedInput-input.MuiInputBase-input": {
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0,
    height: "auto"
  }
};

const checkboxStyles = {
  marginLeft: 0,
  marginRight: 0,
  "& .MuiButtonBase-root.MuiCheckbox-root": {
    padding: 0
  }
};

const boxStyles = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  bgcolor: "#ECF1F8",
  p: 2,
  borderRadius: 4
};

const titleStyles = {
  display: "flex",
  flexDirection: "row",
  gap: 2,
  mb: 2,
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid #e0e0e0"
};

const API_ERROR_KEY_TO_FORMIK: Record<string, string> = {
  title: "refinanceDealName",
  date: "effectiveDate",
  principal_amount: "principalAmount",
  interest_amount: "interestAmount",
  vat_amount: "vatAmount",
  currency: "currency"
};

interface PrepaymentDrawerProps {
  open: boolean;
  onClose: (updated?: boolean) => void;
  dealId: number;
  amortizationRow: AmortizationRow | null;
  prepayment?: CustomPayment;
}

export interface FeeItem {
  id: string;
  type: FeeType;
  percentage: number;
  amount: number;
  measure_data_id: number | null;
}

export interface PrepaymentFormData {
  id?: number;
  prepaymentType: "full" | "partial" | "refinance";
  effectiveDate: string;
  currency: CurrencyType;
  refinanceDealName: string;

  // Prepayment section
  setManual: boolean;
  totalAmount: number;
  principalAmount: number;
  interestAmount: number;
  vatAmount: number;
  vatRate: number;
  creditFacilityFeeAmount: number;
  includeInterest: boolean;
  includeVAT: boolean;
  includeIndexation: boolean;

  // Measure data ids
  principalId: number | null;
  interestId: number | null;
  vatId: number | null;
  principalIndexationId: number | null;
  pikInterestId: number | null;
  creditFacilityFeeId: number | null;

  // PIK interest
  includePikInterest: boolean;
  pikInterestAmount: number;

  // Fee section
  fees: FeeItem[];
  feeSetManual: boolean;
}

// Given a fixed total, fill by priority: Interest → PIK → VAT → Principal
const recalculateFromTotal = (
  total: number,
  includeInterest: boolean,
  includePikInterest: boolean,
  includeVAT: boolean,
  vatRate: number,
  row: AmortizationRow | null
): { interest: number; pik: number; vat: number; principal: number } => {
  const maxInterest = includeInterest ? calculateInterestAmountByRow(row) : 0;
  const maxPik = includePikInterest ? calculatePIKInterestAmountByRow(row) : 0;
  const effectiveVatRate = includeVAT ? vatRate : 0;
  // Budget for interest+PIK before VAT takes its share: (I+P)(1+rate) ≤ total
  const budgetForIP = effectiveVatRate > 0 ? total / (1 + effectiveVatRate) : total;
  const interest = Math.min(maxInterest, Math.max(0, budgetForIP));
  const pik = Math.min(maxPik, Math.max(0, budgetForIP - interest));
  const vat = (interest + pik) * effectiveVatRate;
  const principal = Math.max(0, total - interest - pik - vat);
  return { interest, pik, vat, principal };
};

// Given a fixed principal, calculate everything else independently from the row
const recalculateFromPrincipal = (
  principal: number,
  includeInterest: boolean,
  includePikInterest: boolean,
  includeVAT: boolean,
  vatRate: number,
  row: AmortizationRow | null
): { interest: number; pik: number; vat: number; total: number } => {
  const interest = includeInterest ? calculateInterestAmountByRow(row) : 0;
  const pik = includePikInterest ? calculatePIKInterestAmountByRow(row) : 0;
  const vat = includeVAT ? (interest + pik) * vatRate : 0;
  const total = principal + interest + pik + vat;
  return { interest, pik, vat, total };
};

const calculateInterestAmountByRow = (amortizationRow?: AmortizationRow | null) => {
  return amortizationRow?.accrued_interest || 0;
};

const calculatePIKInterestAmountByRow = (amortizationRow?: AmortizationRow | null) => {
  return (amortizationRow?.accrued_pik || 0) + (amortizationRow?.side_pik_principal || 0);
};

const calculateFeeAmount = (fee: FeeItem, principalAmount: number) => {
  if (fee.type === "call_protection") {
    return (principalAmount * fee.percentage) / 100;
  }
  return fee.amount;
};

const calculateFeePercentage = (fee: FeeItem, principalAmount: number) => {
  if (fee.type === "call_protection") {
    return (fee.amount / principalAmount) * 100;
  }
  return fee.percentage;
};

const getVatRateByDate = (date: string): number => {
  return date < "2025-01-01" ? 0.17 : 0.18;
};

const Content: React.FC<PrepaymentDrawerProps> = ({ onClose, dealId, amortizationRow, prepayment }) => {
  const dealCurrency = useDealDefaultCurrencySelect();
  const dateFormat = useDateFormatSelect();
  const dealTitle = useDealTitleSelector();
  const dealAssetClass = useDealAssetClassSelect();
  const blankTemplateId = useUserBlankTemplateIdSelector();

  const { errorModal } = useModals();
  const { getAmortizationTableAction, createPrepaymentAction } = useDebtActions();
  const { createDealAction } = useDealActions();
  const { getDealsAction } = useTaskManageActions();

  const { action: fetchAmortization } = useActionHook(getAmortizationTableAction);
  const { action: submitPrepayment, loading: submitting } = useActionHook(createPrepaymentAction);
  const { action: createDeal, loading: creatingDeal } = useActionHook(createDealAction, {
    onSuccess: getDealsAction
  });

  const [isMounting, setIsMounting] = useState(true);
  const [userPaymentSelection, setUserPaymentSelection] = useState<"total" | "principal">("total");
  const [currentAmortizationRow, setCurrentAmortizationRow] = useState<AmortizationRow | null>(amortizationRow);
  const [isRefinanceDrawerOpen, setIsRefinanceDrawerOpen] = useState(false);
  const { rounds } = useMeasureDrawer([dealId]);

  const initialValues: PrepaymentFormData = useMemo(() => {
    const effectiveDate = amortizationRow?.date || "";
    const vatRate = getVatRateByDate(effectiveDate);
    const interestAmount = prepayment ? prepayment.interest_amount : calculateInterestAmountByRow(amortizationRow);
    const pikInterestAmount = prepayment
      ? prepayment.pik_interest_amount
      : calculatePIKInterestAmountByRow(amortizationRow);
    const vatAmount = prepayment ? prepayment.vat_amount || 0 : (interestAmount + pikInterestAmount) * vatRate;
    const principalAmount = prepayment ? prepayment.principal_amount : amortizationRow?.principal_payment || 0;
    const includeInterest = prepayment ? !!prepayment.interest_amount : !!amortizationRow?.accrued_interest;
    const includeVAT = prepayment ? !!prepayment.vat_amount : true;
    const includeIndexation = prepayment ? !!prepayment.principal_indexation_amount : false;
    const totalAmount = principalAmount + interestAmount + pikInterestAmount + vatAmount;
    const currency = prepayment ? prepayment.currency : dealCurrency || "USD";

    const accruedPik = (amortizationRow?.accrued_pik || 0) + (amortizationRow?.side_pik_principal || 0);

    return {
      id: prepayment?.id ?? undefined,
      prepaymentType: "partial",
      effectiveDate: amortizationRow?.date || "",
      currency,
      refinanceDealName: dealTitle ? `${dealTitle} - refinance` : "",
      setManual: false,
      totalAmount,
      principalAmount,
      interestAmount,
      pikInterestAmount,
      vatAmount,
      vatRate,
      creditFacilityFeeAmount: prepayment?.fee_amount || 0,
      includeInterest,
      includeVAT,
      includeIndexation,
      principalId: prepayment?.principal_measure_data_id || null,
      interestId: prepayment?.interest_measure_data_id || null,
      vatId: prepayment?.vat_measure_data_id || null,
      principalIndexationId: prepayment?.principal_indexation_measure_data_id || null,
      pikInterestId: prepayment?.pik_interest_measure_data_id || null,
      creditFacilityFeeId: prepayment?.fee_measure_data_id || null,
      includePikInterest: prepayment ? !!prepayment.pik_interest_amount : !!accruedPik,
      fees:
        prepayment?.fees.map((fee) => ({
          id: fee.id.toString(),
          type: fee.type as FeeType,
          percentage: fee.is_percentage ? fee.amount : 0,
          amount: fee.is_percentage ? 0 : fee.amount,
          measure_data_id: fee.fee_measure_data_id || null
        })) || [],
      feeSetManual: false
    };
  }, [prepayment, amortizationRow, dealCurrency, dealTitle]);

  const formik = useFormik<PrepaymentFormData>({
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    validationSchema: prepaymentValidationSchema,
    onSubmit: async () => {
      setIsRefinanceDrawerOpen(true);
    }
  });

  const fetchAmortizationForDate = useCallback(
    async (date: string) => {
      if (!dealId) return;

      try {
        const data = await fetchAmortization(dealId, date);
        const rowForDate = data.table.rows.find((row) => row.date === date);
        if (rowForDate) {
          setCurrentAmortizationRow(rowForDate);
        }
      } catch (error) {
        console.error("Error fetching amortization data:", error);
      }
    },
    [dealId, fetchAmortization]
  );

  const calculateFinalTotalSum = () => {
    const paymentAmount =
      userPaymentSelection === "total"
        ? formik.values.totalAmount
        : sum([
            formik.values.principalAmount,
            formik.values.pikInterestAmount,
            formik.values.interestAmount,
            formik.values.vatAmount
          ]);
    const feeAmount = formik.values.fees.reduce((total, fee) => total + fee.amount, 0);
    return paymentAmount + feeAmount;
  };

  // Recalculate fields when row or prepayment type changes
  useEffect(() => {
    if (!isMounting && currentAmortizationRow && !formik.values.setManual) {
      const fullAmount = ["full", "refinance"].includes(formik.values.prepaymentType);
      const principalAmount = fullAmount
        ? currentAmortizationRow.starting_principal
        : currentAmortizationRow.principal_payment;

      // Always recalculate from principal when the row or type changes
      const { interest, pik, vat, total } = recalculateFromPrincipal(
        principalAmount,
        formik.values.includeInterest,
        formik.values.includePikInterest,
        formik.values.includeVAT,
        formik.values.vatRate,
        currentAmortizationRow
      );

      formik.setFieldValue("principalAmount", principalAmount);
      formik.setFieldValue("interestAmount", interest);
      formik.setFieldValue("pikInterestAmount", pik);
      formik.setFieldValue("vatAmount", vat);
      formik.setFieldValue("totalAmount", total);
    }
  }, [currentAmortizationRow, formik.values.prepaymentType, formik.values.setManual]);

  // Update fee amounts when percentages change (if not manual)
  useEffect(() => {
    if (!isMounting && !formik.values.feeSetManual) {
      const updatedFees = formik.values.fees.map((fee) => {
        if (fee.type === "call_protection") {
          return {
            ...fee,
            amount: calculateFeeAmount(fee, formik.values.principalAmount)
          };
        }
        return fee;
      });
      formik.setFieldValue("fees", updatedFees);
    }
  }, [formik.values.principalAmount, formik.values.feeSetManual]);

  useEffect(() => {
    setIsMounting(false);
  }, []);

  const handleDateChange = async (date: dayjs.Dayjs) => {
    const formattedDate = date.format("YYYY-MM-DD");
    formik.setFieldValue("effectiveDate", formattedDate);

    const newVatRate = getVatRateByDate(formattedDate);
    if (newVatRate !== formik.values.vatRate) {
      formik.setFieldValue("vatRate", newVatRate);
    }

    // Fetching the new row triggers the useEffect which recalculates everything
    await fetchAmortizationForDate(formattedDate);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    // If items are reordered, toggle the payment mode
    if (result.source.index !== result.destination.index) {
      const newMode = userPaymentSelection === "total" ? "principal" : "total";
      setUserPaymentSelection(newMode);
    }
  };

  const getItemOrder = (): ("total" | "principal")[] => {
    // When paymentMode is "total", total sum comes first
    // When paymentMode is "principal", principal comes first
    return userPaymentSelection === "total" ? ["total", "principal"] : ["principal", "total"];
  };

  const getItemData = (itemType: "total" | "principal") => {
    // When setManual is true, both fields are editable
    // When setManual is false, only the current payment mode field is editable
    const isEditable =
      formik.values.setManual ||
      (itemType === "total" && userPaymentSelection === "total") ||
      (itemType === "principal" && userPaymentSelection === "principal");

    if (itemType === "total") {
      return {
        label: "Total sum",
        value: formik.values.totalAmount,
        isEditable,
        fieldName: "totalAmount"
      };
    } else {
      return {
        label: "Principal",
        value: formik.values.principalAmount,
        isEditable,
        fieldName: "principalAmount"
      };
    }
  };

  const addFee = () => {
    const newFee: FeeItem = {
      id: uuidv4(),
      type: "call_protection",
      percentage: 1.0,
      amount: 0,
      measure_data_id: null
    };
    newFee.amount = calculateFeeAmount(newFee, formik.values.principalAmount);

    formik.setFieldValue("fees", [...formik.values.fees, newFee]);
  };

  const removeFee = (feeId: string) => {
    const updatedFees = formik.values.fees.filter((fee) => fee.id !== feeId);
    formik.setFieldValue("fees", updatedFees);
  };

  const updateFee = (feeId: string, field: keyof FeeItem, value: FeeItem[keyof FeeItem]) => {
    let otherField: keyof FeeItem;
    let calcFunction: (fee: FeeItem, principalAmount: number) => number;
    if (field === "percentage") {
      otherField = "amount";
      calcFunction = calculateFeeAmount;
    } else if (field === "amount") {
      otherField = "percentage";
      calcFunction = calculateFeePercentage;
    }

    const updatedFees = formik.values.fees.map((fee) =>
      fee.id === feeId
        ? {
            ...fee,
            [field]: value,
            ...(otherField && { [otherField]: calcFunction({ ...fee, [field]: value }, formik.values.principalAmount) })
          }
        : fee
    );

    formik.setFieldValue("fees", updatedFees);
  };

  const recalculate = (overrides: Partial<PrepaymentFormData>) => {
    if (formik.values.setManual) return;
    const v = { ...formik.values, ...overrides };
    if (userPaymentSelection === "total") {
      const { interest, pik, vat, principal } = recalculateFromTotal(
        v.totalAmount,
        v.includeInterest,
        v.includePikInterest,
        v.includeVAT,
        v.vatRate,
        currentAmortizationRow
      );
      formik.setFieldValue("interestAmount", interest);
      formik.setFieldValue("pikInterestAmount", pik);
      formik.setFieldValue("vatAmount", vat);
      formik.setFieldValue("principalAmount", principal);
    } else {
      const { interest, pik, vat, total } = recalculateFromPrincipal(
        v.principalAmount,
        v.includeInterest,
        v.includePikInterest,
        v.includeVAT,
        v.vatRate,
        currentAmortizationRow
      );
      formik.setFieldValue("interestAmount", interest);
      formik.setFieldValue("pikInterestAmount", pik);
      formik.setFieldValue("vatAmount", vat);
      formik.setFieldValue("totalAmount", total);
    }
  };

  const handleIncludePikInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("includePikInterest", e.target.checked);
    recalculate({ includePikInterest: e.target.checked });
  };

  const handleIncludeInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("includeInterest", e.target.checked);
    recalculate({ includeInterest: e.target.checked });
  };

  const handleIncludeVATChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("includeVAT", e.target.checked);
    recalculate({ includeVAT: e.target.checked });
  };

  const handleValueChange = (fieldName: string, value: number) => {
    formik.setFieldValue(fieldName, value);
    if (!formik.values.setManual) {
      if (fieldName === "totalAmount") {
        const { interest, pik, vat, principal } = recalculateFromTotal(
          value,
          formik.values.includeInterest,
          formik.values.includePikInterest,
          formik.values.includeVAT,
          formik.values.vatRate,
          currentAmortizationRow
        );
        formik.setFieldValue("interestAmount", interest);
        formik.setFieldValue("pikInterestAmount", pik);
        formik.setFieldValue("vatAmount", vat);
        formik.setFieldValue("principalAmount", principal);
      } else if (fieldName === "principalAmount") {
        const { interest, pik, vat, total } = recalculateFromPrincipal(
          value,
          formik.values.includeInterest,
          formik.values.includePikInterest,
          formik.values.includeVAT,
          formik.values.vatRate,
          currentAmortizationRow
        );
        formik.setFieldValue("interestAmount", interest);
        formik.setFieldValue("pikInterestAmount", pik);
        formik.setFieldValue("vatAmount", vat);
        formik.setFieldValue("totalAmount", total);
      }
    }
  };

  const handleRefinanceDrawerClose = () => {
    setIsRefinanceDrawerOpen(false);
    onClose(true);
  };

  const createPrepayments = async (): Promise<CustomPayment> => {
    try {
      // If refinance, create a new deal first
      if (formik.values.prepaymentType === "refinance") {
        await createDeal({
          title: formik.values.refinanceDealName,
          asset_class_id: dealAssetClass!.id,
          sub_asset_class_id: dealAssetClass!.sub_asset_class?.[0]?.id,
          asset_type_id: 1,
          template_id: blankTemplateId,
          deal_capital_seeker: []
        });
      }

      const payload: PrepaymentPayload = {
        id: formik.values.id ?? undefined,
        date: formik.values.effectiveDate,
        principal_amount: formik.values.principalAmount,
        interest_amount: formik.values.includeInterest ? formik.values.interestAmount : null,
        vat_amount: formik.values.includeVAT ? formik.values.vatAmount : null,
        principal_indexation_amount: formik.values.includeIndexation ? 0 : null,
        pik_interest_amount: formik.values.includePikInterest ? formik.values.pikInterestAmount : null,
        is_principal_percentage: false,
        fees: formik.values.fees.map((fee) => ({
          id: typeof fee.id === "string" && fee.id.length === 36 ? undefined : parseInt(fee.id), // uuid length is 36
          type: fee.type,
          amount: fee.percentage || fee.amount,
          is_percentage: !!fee.percentage
        })),
        currency: formik.values.currency,
        is_actual: false,
        shortfall: 0
      };

      const responsePrepayment = await submitPrepayment(dealId, payload);

      return responsePrepayment;
    } catch (error) {
      setIsRefinanceDrawerOpen(false);
      const apiError = error as IApiServiceError;
      const msg = apiError.errors
        ? Object.values(apiError.errors).flat().join(", ")
        : (apiError.message ?? "Something went wrong");
      errorModal({ msg });

      const fieldErrors: Record<string, string> = {};
      if (apiError.errors && typeof apiError.errors === "object") {
        Object.entries(apiError.errors).forEach(([key, messages]) => {
          const formikKey = API_ERROR_KEY_TO_FORMIK[key] ?? key;
          if (messages?.length && formik.values.hasOwnProperty(formikKey)) {
            fieldErrors[formikKey] = messages[0];
          }
        });
      }
      formik.setErrors({ ...formik.errors, ...fieldErrors });
      // Object.keys(fieldErrors).forEach((key) => formik.setFieldTouched(key, true));
      throw error;
    }
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Stack sx={{ p: 5, height: "calc(100vh - 50px)" }}>
          <Stack spacing={3} sx={{ overflowY: "auto", overflowX: "hidden", flex: 1 }}>
            {/* General Section */}
            <Stack spacing={2} gap={2} paddingTop={2}>
              <TextField
                select
                name="prepaymentType"
                label="Prepayment Type"
                value={formik.values.prepaymentType}
                onChange={formik.handleChange}
                fullWidth
                size="small"
                sx={textFieldStyles}
              >
                <MenuItem value="partial">Partial Prepayment</MenuItem>
                <MenuItem value="full">Full Prepayment</MenuItem>
                <MenuItem value="refinance">Refinance</MenuItem>
              </TextField>

              {formik.values.prepaymentType === "refinance" && (
                <TextField
                  name="refinanceDealName"
                  label="Deal Name"
                  value={formik.values.refinanceDealName}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.refinanceDealName)}
                  helperText={formik.errors.refinanceDealName}
                  fullWidth
                  size="small"
                  sx={inputStyles}
                />
              )}

              <Box sx={{ display: "grid", gridTemplateColumns: "4fr 1fr", gap: 3 }}>
                <DatePicker
                  name="effectiveDate"
                  label="Effective Date"
                  format={dateFormat}
                  views={["year", "month", "day"]}
                  value={formik.values.effectiveDate ? dayjs(formik.values.effectiveDate) : null}
                  onChange={(value) => value && handleDateChange(value)}
                  sx={{ width: "100%", ...inputStyles }}
                  slotProps={{
                    textField: {
                      error: formik.touched.effectiveDate && Boolean(formik.errors.effectiveDate),
                      helperText: formik.touched.effectiveDate && formik.errors.effectiveDate
                    }
                  }}
                />

                <TextField
                  select
                  name="currency"
                  label="Currency"
                  value={formik.values.currency}
                  onChange={formik.handleChange}
                  fullWidth
                  size="small"
                  sx={textFieldStyles}
                >
                  {currenciesArray.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            {/* Balance Summary */}
            {currentAmortizationRow && (
              <Box sx={{ bgcolor: "#ECF1F8", p: 2, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Balance
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding Principal
                  </Typography>
                  <Typography variant="body2" textAlign="right">
                    {currencySign[formik.values.currency as CurrencyType]}
                    {currentAmortizationRow.starting_principal.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Accrued Interest
                  </Typography>
                  <Typography variant="body2" textAlign="right">
                    {currencySign[formik.values.currency as CurrencyType]}
                    {currentAmortizationRow.accrued_interest.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </Typography>
                  {!!(currentAmortizationRow.accrued_pik || currentAmortizationRow.side_pik_principal) && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Accrued PIK
                      </Typography>
                      <Typography variant="body2" textAlign="right">
                        {currencySign[formik.values.currency as CurrencyType]}
                        {(
                          (currentAmortizationRow.accrued_pik || 0) + (currentAmortizationRow.side_pik_principal || 0)
                        ).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* Prepayment Section */}
            <Stack spacing={2}>
              <Stack sx={titleStyles}>
                <Typography variant="h6" fontSize={14}>
                  Prepayment
                </Typography>

                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={formik.values.setManual}
                      onChange={(e) => formik.setFieldValue("setManual", e.target.checked)}
                    />
                  }
                  label="Set Manual"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: 14
                    }
                  }}
                />
              </Stack>

              <Box sx={{ position: "relative" }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="payment-items">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {getItemOrder().map((itemType, index) => {
                          const itemData = getItemData(itemType);
                          return (
                            <Draggable key={itemType} draggableId={itemType} index={index}>
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    ...boxStyles,
                                    mb: index === 0 ? 8 : 0,
                                    transform: snapshot.isDragging ? "rotate(3deg)" : "none",
                                    boxShadow: snapshot.isDragging ? 3 : 0,
                                    transition: "transform 0.2s ease, box-shadow 0.2s ease"
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 120 }}>
                                    <Box {...provided.dragHandleProps}>
                                      <DragIndicatorIcon sx={{ fontSize: 16, color: "#666", cursor: "grab" }} />
                                    </Box>
                                    <Typography variant="body2">{itemData.label}</Typography>
                                  </Box>
                                  <NumberFormat
                                    fullWidth
                                    name={itemData.fieldName}
                                    thousandSeparator
                                    isNumericString
                                    variant="outlined"
                                    prefix={currencySign[formik.values.currency as CurrencyType]}
                                    decimalScale={2}
                                    allowNegative={false}
                                    disabled={!itemData.isEditable}
                                    customInput={TextField}
                                    value={itemData.value}
                                    onValueChange={({ value }) => {
                                      if (itemData.isEditable && itemData.fieldName) {
                                        handleValueChange(itemData.fieldName, parseFloat(value) || 0);
                                      }
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        bgcolor: itemData.isEditable ? "white" : "transparent"
                                      },
                                      ...inputStyles
                                    }}
                                  />
                                </Box>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <Box sx={{ borderBottom: "1px solid", position: "absolute", width: "100%", top: "50%" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                      bgcolor: "background.paper",
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "text.secondary"
                    }}
                  >
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2">Calculate</Typography>
                    <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              </Box>

              <Box sx={boxStyles}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.includeInterest}
                      onChange={handleIncludeInterestChange}
                      name="includeInterest"
                    />
                  }
                  label="Interest"
                  sx={{ minWidth: 120, ...checkboxStyles }}
                />
                <NumberFormat
                  fullWidth
                  thousandSeparator
                  isNumericString
                  variant="outlined"
                  prefix={currencySign[formik.values.currency as CurrencyType]}
                  decimalScale={2}
                  disabled={!formik.values.setManual}
                  customInput={TextField}
                  value={formik.values.interestAmount}
                  onValueChange={({ value }) => {
                    if (formik.values.setManual) {
                      formik.setFieldValue("interestAmount", parseFloat(value) || 0);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: formik.values.setManual ? "white" : "transparent"
                    },
                    ...inputStyles
                  }}
                />
              </Box>

              <Box sx={boxStyles}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.includePikInterest}
                      onChange={handleIncludePikInterestChange}
                      name="includePikInterest"
                    />
                  }
                  label="PIK Interest"
                  sx={{ minWidth: 120, ...checkboxStyles }}
                />
                <NumberFormat
                  fullWidth
                  thousandSeparator
                  isNumericString
                  variant="outlined"
                  prefix={currencySign[formik.values.currency as CurrencyType]}
                  decimalScale={2}
                  disabled={!formik.values.setManual}
                  customInput={TextField}
                  value={formik.values.pikInterestAmount}
                  onValueChange={({ value }) => {
                    if (formik.values.setManual) {
                      formik.setFieldValue("pikInterestAmount", parseFloat(value) || 0);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: formik.values.setManual ? "white" : "transparent"
                    },
                    ...inputStyles
                  }}
                />
              </Box>

              <Box sx={boxStyles}>
                <FormControlLabel
                  control={
                    <Checkbox checked={formik.values.includeVAT} onChange={handleIncludeVATChange} name="includeVAT" />
                  }
                  label="VAT"
                  sx={{ minWidth: 120, ...checkboxStyles }}
                />
                <NumberFormat
                  fullWidth
                  thousandSeparator
                  isNumericString
                  variant="outlined"
                  prefix={currencySign[formik.values.currency as CurrencyType]}
                  decimalScale={2}
                  disabled={!formik.values.setManual}
                  customInput={TextField}
                  value={formik.values.vatAmount}
                  onValueChange={({ value }) => {
                    if (formik.values.setManual) {
                      formik.setFieldValue("vatAmount", parseFloat(value) || 0);
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: formik.values.setManual ? "white" : "transparent"
                    },
                    ...inputStyles
                  }}
                />
                <TextField
                  type="number"
                  variant="outlined"
                  value={formik.values.vatRate}
                  disabled={!formik.values.setManual}
                  onChange={(e) => {
                    const newVatRate = parseFloat(e.target.value) || 0;
                    formik.setFieldValue("vatRate", newVatRate);
                    if (!formik.values.setManual) {
                      recalculate({ vatRate: newVatRate });
                    }
                  }}
                  sx={{
                    width: 120,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: formik.values.setManual ? "white" : "transparent"
                    },
                    ...inputStyles
                  }}
                />
              </Box>

              <Box sx={boxStyles}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.includeIndexation}
                      onChange={formik.handleChange}
                      name="includeIndexation"
                      disabled
                    />
                  }
                  label="Principal Indexation"
                  sx={{ minWidth: 120, ...checkboxStyles }}
                />
                <NumberFormat
                  fullWidth
                  thousandSeparator
                  isNumericString
                  variant="outlined"
                  prefix={currencySign[formik.values.currency as CurrencyType]}
                  decimalScale={2}
                  disabled
                  customInput={TextField}
                  value={0}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "transparent"
                    },
                    ...inputStyles
                  }}
                />
              </Box>
            </Stack>

            {/* Fee Section */}
            <Stack spacing={2}>
              <Stack sx={titleStyles}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" fontSize={14}>
                    Fee
                  </Typography>
                  <HelpOutlineIcon sx={{ fontSize: 16, color: "info.main" }} />
                </Box>

                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={formik.values.feeSetManual}
                      onChange={(e) => formik.setFieldValue("feeSetManual", e.target.checked)}
                    />
                  }
                  label="Set Manual"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: 14
                    }
                  }}
                />
              </Stack>

              {formik.values.fees.map((fee) => (
                <Box key={fee.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <TextField
                    select
                    label="Type"
                    value={fee.type}
                    onChange={(e) => updateFee(fee.id, "type", e.target.value)}
                    size="small"
                    sx={{ ...textFieldStyles, flex: 1 }}
                  >
                    <MenuItem value="call_protection">Call protection</MenuItem>
                    <MenuItem value="yield_maintenance">Yield maintenance</MenuItem>
                    <MenuItem value="fixed_amount">Fixed amount</MenuItem>
                  </TextField>
                  <NumberFormat
                    label="Percentage"
                    thousandSeparator
                    isNumericString
                    variant="outlined"
                    suffix="%"
                    decimalScale={2}
                    disabled={formik.values.feeSetManual || fee.type === "fixed_amount"}
                    customInput={TextField}
                    value={fee.percentage}
                    onValueChange={({ value }, sourceInfo) => {
                      if (sourceInfo.source === "event") {
                        updateFee(fee.id, "percentage", parseFloat(value) || 0);
                      }
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: !formik.values.feeSetManual ? "white" : "transparent"
                      },
                      ...inputStyles
                    }}
                  />
                  <NumberFormat
                    label="Amount"
                    thousandSeparator
                    isNumericString
                    variant="outlined"
                    prefix={currencySign[formik.values.currency as CurrencyType]}
                    decimalScale={0}
                    disabled={!formik.values.feeSetManual && fee.type !== "fixed_amount"}
                    customInput={TextField}
                    value={fee.amount}
                    onValueChange={({ value }, sourceInfo) => {
                      if (sourceInfo.source === "event") {
                        updateFee(fee.id, "amount", parseFloat(value) || 0);
                      }
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: formik.values.feeSetManual ? "white" : "transparent"
                      },
                      ...inputStyles
                    }}
                  />
                  <IconButton onClick={() => removeFee(fee.id)} sx={{ color: "error.main" }}>
                    <RemoveIcon />
                  </IconButton>
                </Box>
              ))}

              <Button
                variant="outlined"
                onClick={addFee}
                sx={{ alignSelf: "flex-start", fontSize: 14, height: 24, minHeight: 24 }}
              >
                Add Fee
              </Button>
            </Stack>

            {/* Total Sum Section */}
            <Stack spacing={2}>
              <Box sx={titleStyles}>
                <Typography variant="h6" fontSize={14}>
                  Total Sum
                </Typography>
              </Box>
              <NumberFormat
                name="totalSum"
                label="Sum"
                thousandSeparator
                isNumericString
                variant="outlined"
                prefix={currencySign[formik.values.currency as CurrencyType]}
                decimalScale={0}
                disabled
                customInput={TextField}
                value={calculateFinalTotalSum()}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "transparent"
                  },
                  ...inputStyles
                }}
              />
            </Stack>
          </Stack>
          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
            <Button variant="outlined" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={submitting || creatingDeal}>
              Next
            </Button>
          </Box>
        </Stack>
      </form>

      {isRefinanceDrawerOpen && rounds && (
        <PrepaymentDrawerValues
          rounds={rounds}
          open={isRefinanceDrawerOpen}
          onClose={handleRefinanceDrawerClose}
          dealId={dealId}
          createPrepayments={createPrepayments}
          prepaymentFormData={formik.values}
        />
      )}
    </>
  );
};

export const PrepaymentDrawer: React.FC<PrepaymentDrawerProps> = ({
  open,
  onClose,
  dealId,
  amortizationRow,
  prepayment
}) => {
  return (
    <Drawer anchor="left" open={open} handleClose={() => onClose()} title="Add Value">
      {dealId && (
        <AsyncContentMui isLoading={false}>
          <Content
            open={open}
            onClose={onClose}
            dealId={dealId}
            amortizationRow={amortizationRow}
            prepayment={prepayment}
          />
        </AsyncContentMui>
      )}
    </Drawer>
  );
};
